import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const navItems = [
    { label: 'Forside', path: '/' },
    { label: 'Om os', path: '/om-os' },
    { label: 'Kontakt', path: '/kontakt' }
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100 py-4' 
        : 'bg-white border-b border-transparent py-6'
    }`}>
      <div className="mx-auto max-w-7xl px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group focus:outline-none">
          <span className="font-display text-2xl font-bold text-navy-950 tracking-tight transition-colors duration-300 group-hover:text-navy-800">
            Know<span className="text-gold-500">How</span> <span className="font-semibold">GROUP</span>
          </span>
        </Link>

        {/* Desktop Navigation (Visible >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-10">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`relative py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors duration-300 hover:text-gold-500 focus:outline-none ${
                isActive(item.path) ? 'text-gold-500' : 'text-navy-700'
              }`}
            >
              {item.label}
              {isActive(item.path) && (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-gold-500 rounded-full" />
              )}
            </Link>
          ))}
          <Link
            to="/kontakt"
            className="ml-4 px-6 py-2.5 bg-navy-900 text-white text-xs font-bold uppercase tracking-[0.2em] rounded-none hover:bg-gold-500 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:ring-offset-2"
          >
            Kontakt os
          </Link>
        </nav>

        {/* Burger Menu Button (Visible < 1200px) */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex min-[1200px]:hidden items-center justify-center w-10 h-10 text-navy-950 hover:text-gold-500 transition-colors duration-300 focus:outline-none"
          aria-label="Toggle menu"
          aria-expanded={isOpen}
        >
          <i className={`fa-light ${isOpen ? 'fa-xmark text-xl' : 'fa-bars text-xl'}`} />
        </button>
      </div>

      {/* Mobile Navigation Drawer (Visible < 1200px) */}
      <div
        className={`fixed inset-x-0 top-[73px] md:top-[81px] bg-white border-b border-slate-100 shadow-lg transition-all duration-300 ease-in-out origin-top min-[1200px]:hidden z-40 ${
          isOpen ? 'scale-y-100 opacity-100 visible' : 'scale-y-95 opacity-0 invisible pointer-events-none'
        }`}
      >
        <nav className="flex flex-col px-6 py-8 gap-6 bg-white">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`text-xs font-semibold uppercase tracking-[0.15em] py-2 border-b border-slate-50 transition-colors duration-300 ${
                isActive(item.path) ? 'text-gold-500 pl-2 border-l-2 border-l-gold-500' : 'text-navy-700 hover:text-gold-500'
              }`}
            >
              {item.label}
            </Link>
          ))}
          <Link
            to="/kontakt"
            className="mt-4 w-full py-3 bg-navy-900 text-white text-center text-xs font-bold uppercase tracking-[0.2em] rounded-none hover:bg-gold-500 transition-all duration-300"
          >
            Kontakt os
          </Link>
        </nav>
      </div>
    </header>
  );
}
